import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import Row from 'components/common/Row';
import Text from 'components/common/Text';
import Column from 'components/common/Column';
import SaleCardV2 from './components/SaleCard.v2';
import PageSizeSelector from './components/PageSizeSelector';
import Loading from 'components/Loading/Loading';
import { OrderFilterInput } from './graphQL/types';
import { OrderConnection } from 'types/api';
import PaginationContainer from 'containers/QnaList/components/PaginationContainer';

import RefreshButton from 'components/RefreshButton/RefreshButton';

const ProductItemListContainer = styled.ul`
  display: flex;
  flex-direction: column;

  height: 100%;

  padding: 0px;
  padding-bottom: 64px;
  margin: 0;

  > li {
    list-style: none;

    &:nth-last-of-type(1) {
      margin-bottom: 64px;
    }
  }
`;

const EmptyBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 100%;
  height: 100%;

  font-size: 16px;

  p {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    text-align: center;

    color: #46474c;
  }
`;

const RefreshDataContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 100%;

  margin: 40px auto 0px;
  padding-bottom: 64px;

  > button {
    display: flex;
    gap: 4px;
    align-items: center;

    width: fit-content;
    height: 36px;

    padding: 0px 12px;
    margin: 0px auto;

    border-radius: 8px;
    border: 0;

    font-weight: 600;
    font-size: 14px;
    line-height: 36px;
    color: rgba(70, 71, 76, 1);

    background-color: rgba(244, 244, 245, 1);

    >img {
      width: 14px,
      height: 14px,

      pointer-events: 'none',
    } 
  }
`;

interface Props {
  orderItems: OrderConnection;
  isOpenCountFilter: boolean;
  orderFilterInput: OrderFilterInput;
  handleSetOrderFilterInput: (key: string, value: any) => void;
  handleIsOpenCountFilter: () => void;
  isLoading: boolean;
  selectedSaleId: string | null;
  handleSelectSaleId: (id: string) => void;
  handleRefreshData: () => void;
  isMobile: boolean;
}

function SoldProductContent({
  orderItems,
  isOpenCountFilter,
  orderFilterInput,
  handleSetOrderFilterInput,
  handleIsOpenCountFilter,
  isLoading,
  selectedSaleId,
  handleSelectSaleId,
  handleRefreshData,
  isMobile,
}: Props) {
  const flattedOrderItems = orderItems?.edges.map(edge => edge.node);
  const containerRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [orderFilterInput.page]);

  return (
    <>
      <Row
        justifyContent="space-between"
        style={{
          marginBottom: '8px',
          padding: isMobile ? '0px 20px' : '0px 40px',
        }}
      >
        <Text
          weight={600}
          size="16px"
          style={{ lineHeight: '24px' }}
          color="rgba(27, 28, 30, 1)"
        >
          전체{' '}
          {orderItems?.totalCount ? orderItems.totalCount.toLocaleString() : 0}
          건
        </Text>

        <PageSizeSelector
          isOpenCountFilter={isOpenCountFilter}
          filterItem={orderFilterInput}
          handleSetFilterItem={handleSetOrderFilterInput}
          handleIsOpenCountFilter={handleIsOpenCountFilter}
        />
      </Row>

      {flattedOrderItems && (
        <ProductItemListContainer ref={containerRef}>
          {isLoading ? (
            <Column align="center" style={{ minHeight: '50vh' }}>
              <Loading type="flex" />
            </Column>
          ) : flattedOrderItems.length === 0 ? (
            <EmptyBox>판매 내역이 없어요</EmptyBox>
          ) : (
            flattedOrderItems.map(salesInfo => {
              return (
                <li key={salesInfo.id}>
                  <SaleCardV2
                    {...salesInfo}
                    isSelected={selectedSaleId === salesInfo.id}
                    handleSelectSaleId={handleSelectSaleId}
                    isMobile={isMobile}
                  />
                </li>
              );
            })
          )}
          <PaginationContainer
            totalCount={orderItems?.totalCount}
            page={orderFilterInput?.page}
            size={orderFilterInput?.size}
            handleSetQnaFilter={handleSetOrderFilterInput}
            useButtonText={false}
          />

          <RefreshDataContainer>
            <RefreshButton handleRefreshData={handleRefreshData} />
          </RefreshDataContainer>
        </ProductItemListContainer>
      )}
    </>
  );
}

export default SoldProductContent;
